import { useDispatch, useSelector } from 'react-redux';

import { RootState } from 'src/store/reducers';
import HorizontalTile from '@components/styled-UI/HorizontalTile';
import Alert from '@components/styled-UI/Alert';

import LoaderTable from '@loaders/LoaderTable';

import PaymentDetailsOrderDetail from './online-orders/PaymentDetailsOrderDetail';

import { IGlobalOrderDetails } from '@interfaces/myPurchasesInterfaces';

import { getLabelFallback, isOrderClosed } from '@utils/utils';

import URLS from '@utils/urlList';

import OnlineOrder from '@images/svg-icons/onlinePrescription.svg'

interface Props {
    orderDetail: IGlobalOrderDetails;
}

const OrderDetailOverview = ({ orderDetail }: Props): JSX.Element => {
    const {
        orderNumber,
        orderDate,
        orderStatus,
        readyForPickUpAddress,
        readyForPickUpPhone,
        quoteAddress,
        hasContactLenses,
        billingAddressId,
    } = orderDetail;

    const dispatch = useDispatch();

    const config = useSelector((state: RootState) => state.common.config);
    const globalOrderDetailsRequestStatus = useSelector(
        (state: RootState) => state.myPurchases.globalOrderDetailsRequestStatus
    );

    const urlDownloadReceipt = URLS.DOWNLOAD_RECEIPT_METHOD.replace('{storeId}', config.storeId).replace('{orderId}', orderNumber);

    const renderSuggestion = (): JSX.Element => {
        return (
            <div className="primary-message medium align-left suggestion">
                {getLabelFallback('MYPURCHASES_ONLINE_ORDERS_ORDER_DETAILS_COMPLETED_1_SUGGEST')}
                <a
                    href={'/to-us/contact-us'}                  
                >
                    {getLabelFallback(
                        'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAILS_COMPLETED_SUGGEST_PLACEHOLDER'
                    )}
                </a>
                {getLabelFallback('MYPURCHASES_ONLINE_ORDERS_ORDER_DETAILS_COMPLETED_2_SUGGEST')}
				<a
                    href={'/to-us/returns'}                  
                >
                    {getLabelFallback(
                        'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAILS_COMPLETED_SUGGEST_PLACEHOLDER_RETURN'
                    )}
                </a>
            </div>
        );
    };

    const renderSuggestionTop = (): JSX.Element => {
        return (
            <div className="primary-message medium align-left suggestion-top">
                {getLabelFallback('MYPURCHASES_ONLINE_ORDERS_ORDER_DETAILS_COMPLETED_TOP_SUGGEST')}
                <a
                    href="#"
                    onClick={() =>
                        window.open(
                            config.callFromComponentToTO.myPurchases.inStorePurchases.trustpilot,
                            '_parent'
                        )
                    }
                >
                    {getLabelFallback(
                        'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAILS_COMPLETED_SUGGEST_TOP_PLACEHOLDER'
                    )}
                </a>
            </div>
        );
    };

    const renderQuotationAddress = (): JSX.Element => {
        return (
            <div className="quotation-address primary-message medium align-left">
                <span>
                    {getLabelFallback(
                        'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAILS_QUOTE_ADDRESS_LABEL'
                    )}
                </span>
                <span className=" primary-message medium align-left bold">{quoteAddress}</span>
            </div>
        );
    };
    return (
        <div role="region" aria-label="Order detail overview" className="order-detail-overview">
            <div className="order-online-text">
                <OnlineOrder />
                <span>{getLabelFallback('MYPROFILE_MY_ORDERS_ONLINE_LABEL')}</span>
            </div>
            <div className="order-recap">
                <div className="order-recap-left">
                    <div className="row">
                        <div className="label">
                            {getLabelFallback(
                                'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_ORDER_NUMBER_LABEL'
                            )}
                        </div>
                        <div className="value">{orderNumber}</div>
                    </div>
                    <div className="row">
                        <div className="label">
                            {getLabelFallback(
                                'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_ORDER_DATE_LABEL'
                            )}
                        </div>
                        <div className="value">{orderDate}</div>
                    </div>
                </div>
                <div className="order-recap-right">
                    {isOrderClosed(orderDetail) && (
                        <a
                            href="#"
                            data-element-id="OrderDetails_PrintLabel"
                            onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                window.location.href = urlDownloadReceipt;
                            }}
                        >
                            {getLabelFallback('MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_LINK_LABEL')}
                        </a>
                    )}
                    {/*hasContactLenses && orderStatus === 'D' && (
                        <button
                            className="primary-button button-size-small"
                            onClick={() =>
                                handleContactLensesReorder(
                                    +orderNumber,
                                    +config.storeId,
                                    +config.catalogId,
                                    config.langId,
                                    billingAddressId
                                )
                            }
                        >
                            {getLabelFallback(
                                'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_REORDER_NOW_CTA_LABEL'
                            )}
                        </button>
                    )*/}

                    {/**TODO: Handle future CR */}
                    {/*  <button
                        className="primary-button button-size-small"
                        data-element-id="OrderDetails_TrackMyOrder"
                    >
                        {getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAIL_TRACK_YOUR_ORDER_CTA_LABEL'
                        )}
                    </button> */}
                </div>
            </div>
            {quoteAddress && renderQuotationAddress()}
            {(readyForPickUpAddress || readyForPickUpPhone) && (
                <Alert customStyle="info">
                    <div className="pickup-info-alert">
                        <span className="primary-message bold medium align-left">
                            {getLabelFallback(
                                'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAILS_READY_FOR_PICKUP_TITLE'
                            )}
                        </span>
                        {readyForPickUpAddress && (
                            <div className="primary-message small align-left">
                                {readyForPickUpAddress}
                            </div>
                        )}
                        <div className="contacts">
                            {readyForPickUpPhone && (
                                <a
                                    href={`tel:${readyForPickUpPhone}`}
                                    data-element-id="OrderDetails_PhoneNum"
                                    className="primary-message small align-left"
                                >
                                    {readyForPickUpPhone.replace(
                                        /(\d{3})(\d{3})(\d{4})/,
                                        '($1) $2-$3'
                                    )}
                                </a>
                            )}
                            {readyForPickUpAddress && (
                                <a
                                    href={`http://maps.google.com/?q=${readyForPickUpAddress}`}
                                    target="_blank"
                                    data-element-id="OrderDetails_GetDirections"
                                    className="primary-message small align-right"
                                >
                                    {getLabelFallback(
                                        'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAILS_READY_FOR_PICKUP_ADDRESS_PLACEHOLDER'
                                    )}
                                </a>
                            )}
                        </div>
                    </div>
                </Alert>
            )}
            {orderDetail.orderStatus === 'D' && renderSuggestionTop()}
            {globalOrderDetailsRequestStatus === 'LOADING' && (
                <LoaderTable className="order-detail-loader" />
            )}
            {globalOrderDetailsRequestStatus === 'SUCCESS' && orderDetail?.orderItems?.length && (
                <div className="horizontal-tile-container">
                    {orderDetail.orderItems.map(orderItem => (
                        <HorizontalTile
                            key={orderItem.orderItemId}
                            orderItem={orderItem}
                            orderId={orderNumber}
                            orderAddressEmail={orderItem.email1}
                            billingAddressId={billingAddressId}
                            order={orderDetail}
                        />
                    ))}
                </div>
            )}
            <PaymentDetailsOrderDetail orderDetail={orderDetail} />
            {orderDetail.orderStatus === 'D' && renderSuggestion()}
        </div>
    );
};
export default OrderDetailOverview;
